import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PostCard from "./PostCard";
import { X, Filter as FilterIcon, Loader2 } from "lucide-react";
import { SentimentLine, RiskBar } from "./charts";

/**
 * Drill panel shown at the bottom of the dashboard when a node in the
 * sentiment tree or a marker is clicked. It fetches the matching posts
 * and aggregate trend, and lets the user dismiss.
 */
export default function DrillPanel({ selection, onClose, onOpenPost, onJumpToMap }) {
  const [posts, setPosts] = useState([]);
  const [trend, setTrend] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selection) return;
    setLoading(true);
    const q = new URLSearchParams();
    if (selection.venture) q.set("venture", selection.venture);
    if (selection.category) q.set("category", selection.category);
    if (selection.severity) q.set("severity", selection.severity);
    if (selection.state) q.set("state", selection.state);
    if (selection.site) {
      // backend doesn't filter by site name directly - we filter client-side from fetched feed
    }
    q.set("limit", "80");
    Promise.all([
      api.get(`/posts?${q}`),
      selection.category ? api.get(`/sentiment-trend?bucket=day&category=${encodeURIComponent(selection.category)}`) : api.get(`/sentiment-trend?bucket=day`),
    ]).then(([p, t]) => {
      let items = p.data.items;
      if (selection.site) items = items.filter((x) => x.site === selection.site);
      if (selection.sub) items = items.filter((x) => x.activity_subpoint === selection.sub);
      setPosts(items);
      setTotal(selection.site || selection.sub ? items.length : p.data.total);
      setTrend(t.data.series.slice(-30));
    }).finally(() => setLoading(false));
  }, [selection]);

  if (!selection) return null;

  const crumbs = [];
  if (selection.venture) crumbs.push(selection.venture);
  if (selection.site) crumbs.push(selection.site);
  if (selection.category) crumbs.push(selection.category);
  if (selection.sub) crumbs.push(selection.sub);
  if (selection.state) crumbs.push(selection.state);
  if (selection.severity) crumbs.push(`Severity: ${selection.severity}`);

  return (
    <section className="border border-slate-200 bg-white m-4 mt-0" data-testid="drill-panel">
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div className="min-w-0">
          <div className="kicker">Drill-down · related signals</div>
          <h3 className="font-display text-[15px] font-semibold tracking-tight flex flex-wrap items-center gap-2 mt-1">
            <FilterIcon className="h-3.5 w-3.5 text-slate-400" />
            {crumbs.map((c, i) => (
              <React.Fragment key={i}>
                <span className="truncate max-w-[280px]">{c}</span>
                {i < crumbs.length - 1 && <span className="text-slate-300">›</span>}
              </React.Fragment>
            ))}
          </h3>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-mono text-slate-500">{total} signals</span>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="drill-close">
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="grid grid-cols-12 gap-0 border-b border-slate-100">
        <div className="col-span-12 lg:col-span-6 p-4 border-r border-slate-100">
          <div className="kicker mb-2">Sentiment trajectory</div>
          <SentimentLine data={trend} />
        </div>
        <div className="col-span-12 lg:col-span-6 p-4">
          <div className="kicker mb-2">Risk intensity</div>
          <RiskBar data={trend} />
        </div>
      </div>

      {loading ? (
        <div className="p-8 flex items-center justify-center text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> loading related signals…
        </div>
      ) : posts.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500">No matching signals.</div>
      ) : (
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 max-h-[540px] overflow-auto thin-scroll">
          {posts.slice(0, 30).map((p) => (
            <PostCard key={p.id} post={p} onClick={onOpenPost} onJumpToMap={onJumpToMap} />
          ))}
        </div>
      )}
    </section>
  );
}
